import { Fancybox } from '@fancyapps/ui';
import '@fancyapps/ui/dist/fancybox/fancybox.css';
import { ChevronLeft, Download } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { toast } from 'react-toastify';
import { fetchGalleryDetailBySlug } from '../api/galleries';
import { GalleryDownloadConfirmModal } from '../components/GalleryDownloadConfirmModal';
import { PageSeo } from '../components/PageSeo';
import { SubpageLayout } from '../components/SubpageLayout';
import { useAuth } from '../context/AuthContext';
import { getGalleryBySlug } from '../data/galleries';
import { useGalleryDownload } from '../hooks/useGalleryDownload';
import { downloadSingleGalleryImage } from '../utils/galleryDownload';
import { stripHtmlForMeta, truncateMetaDescription } from '../utils/metaText';

/**
 * Tek albüm — URL: /member-area/galleries/:gallerySlug
 * Statik slug veya canlı API (`AllGallery/GetById/{id}`).
 */
export function GalleryDetailPage() {
    const { t } = useTranslation();
    const { gallerySlug } = useParams();
    const { authToken, isSimulatedAuth } = useAuth();
    const useLiveApi = Boolean(authToken && !isSimulatedAuth);

    const staticGallery = gallerySlug
        ? getGalleryBySlug(gallerySlug)
        : undefined;

    const [listAlbum, setListAlbum] = useState(
        /** @type {import('../data/galleries').GalleryAlbum | null} */ (null),
    );
    const [detail, setDetail] = useState(null);
    const [loadError, setLoadError] = useState(
        /** @type {string | null} */ (null),
    );
    const [loading, setLoading] = useState(
        () => Boolean(useLiveApi && !staticGallery && gallerySlug),
    );
    const [downloadingImageId, setDownloadingImageId] = useState(
        /** @type {string | null} */ (null),
    );

    const {
        downloadModal,
        closeDownloadModal,
        openGalleryDownload,
        handleDownloadConfirm,
    } = useGalleryDownload({ authToken, useLiveApi });

    const fancyboxGroup = useMemo(
        () => `gallery-detail-${gallerySlug || 'album'}`,
        [gallerySlug],
    );

    useEffect(() => {
        if (staticGallery) {
            setListAlbum(null);
            setDetail(null);
            setLoadError(null);
            setLoading(false);
            return undefined;
        }
        if (!gallerySlug || !useLiveApi || !authToken) {
            setListAlbum(null);
            setDetail(null);
            setLoadError(null);
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        setLoading(true);
        setLoadError(null);
        setDetail(null);

        (async () => {
            try {
                const { payload, album } = await fetchGalleryDetailBySlug(
                    authToken,
                    gallerySlug,
                );
                if (cancelled) return;
                setListAlbum(album);
                setDetail(payload);
            } catch (e) {
                if (cancelled) return;
                setListAlbum(null);
                setDetail(null);
                setLoadError(
                    e instanceof Error
                        ? e.message
                        : t('galleriesPage.genericLoadError'),
                );
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [staticGallery, useLiveApi, authToken, gallerySlug, t]);

    const gallery = staticGallery ?? listAlbum;

    const displayTitle =
        detail?.title || listAlbum?.title || staticGallery?.title || '';
    const displayCount =
        detail?.images?.length ??
        listAlbum?.photoCount ??
        staticGallery?.photoCount ??
        0;

    const imageUrls = useMemo(
        () => detail?.images ?? [],
        [detail],
    );

    const downloadAlbum = useMemo(() => {
        if (!gallery) return null;
        return {
            ...gallery,
            title: displayTitle || gallery.title,
        };
    }, [gallery, displayTitle]);

    const preloadedDownloadImages = useMemo(
        () =>
            imageUrls.map((img) => ({
                id: img.id,
                src: img.src,
                alt: img.alt,
            })),
        [imageUrls],
    );

    const handleGalleryDownloadClick = useCallback(() => {
        if (!downloadAlbum) return;
        openGalleryDownload(downloadAlbum, preloadedDownloadImages);
    }, [downloadAlbum, preloadedDownloadImages, openGalleryDownload]);

    const handleSingleImageDownload = useCallback(
        async (img, index) => {
            if (!useLiveApi || !authToken) {
                toast.info(t('galleriesPage.downloadRequiresLiveApi'));
                return;
            }

            setDownloadingImageId(img.id);
            try {
                await downloadSingleGalleryImage(
                    img.src,
                    displayTitle || gallery?.title || '',
                    index,
                    authToken,
                );
            } catch (err) {
                const msg =
                    err instanceof Error
                        ? err.message
                        : t('galleriesPage.downloadError');
                toast.error(msg);
            } finally {
                setDownloadingImageId(null);
            }
        },
        [useLiveApi, authToken, displayTitle, gallery?.title, t],
    );

    useEffect(() => {
        if (imageUrls.length === 0) return undefined;

        const escapedGroup = CSS.escape(fancyboxGroup);
        const selector = `[data-fancybox="${escapedGroup}"]`;

        Fancybox.bind(selector, {
            Carousel: {
                infinite: false,
            },
            compact: false,
            placeFocusBack: true,
        });

        return () => {
            Fancybox.close();
            Fancybox.unbind(selector);
        };
    }, [imageUrls, fancyboxGroup]);

    if (loading) {
        return (
            <>
                <PageSeo
                    title={`${t('galleriesPage.title')} — ${t('header.rotaryAlt')}`}
                    description={t('seo.galleries')}
                    noindex
                />
                <SubpageLayout
                    title={t('galleriesPage.title')}
                    subtitle={t('galleriesPage.loading')}
                    contentClassName="galleries-page"
                >
                    <div role="status" aria-live="polite" aria-busy="true">
                        <span className="visually-hidden">
                            {t('galleriesPage.loading')}
                        </span>
                        <SkeletonTheme
                            baseColor="#e8ecf2"
                            highlightColor="#f0f3f8"
                        >
                            <Skeleton
                                height={26}
                                width="min(72%, 420px)"
                                className="mb-3"
                                borderRadius={8}
                            />
                            <Skeleton count={3} height={13} className="mb-2" />
                        </SkeletonTheme>
                    </div>
                </SubpageLayout>
            </>
        );
    }

    if (loadError) {
        return (
            <>
                <PageSeo
                    title={`${t('galleriesPage.title')} — ${t('header.rotaryAlt')}`}
                    description={t('seo.galleries')}
                    noindex
                />
                <SubpageLayout
                    title={t('galleriesPage.title')}
                    subtitle={t('galleriesPage.loading')}
                    contentClassName="galleries-page"
                >
                    <p className="gallery-detail-placeholder mb-4 text-secondary">
                        {t('galleriesPage.detailLoadError', { error: loadError })}
                    </p>
                    <p className="mb-0">
                        <Link
                            to="/member-area/galleries"
                            className="gallery-detail-back-link d-inline-flex align-items-center gap-1"
                        >
                            <ChevronLeft
                                size={20}
                                strokeWidth={1.85}
                                aria-hidden
                            />
                            {t('galleriesPage.backToGalleries')}
                        </Link>
                    </p>
                </SubpageLayout>
            </>
        );
    }

    if (!gallery) {
        return (
            <>
                <PageSeo
                    title={`${t('galleriesPage.notFoundTitle')} — ${t('header.rotaryAlt')}`}
                    description={t('seo.galleryDetailFallback')}
                    noindex
                />
                <SubpageLayout
                    title={t('galleriesPage.notFoundTitle')}
                    subtitle={t('galleriesPage.notFoundSubtitle')}
                    contentClassName="galleries-page"
                >
                    <p className="mb-0">
                        <Link
                            to="/member-area/galleries"
                            className="gallery-detail-back-link d-inline-flex align-items-center gap-1"
                        >
                            <ChevronLeft
                                size={20}
                                strokeWidth={1.85}
                                aria-hidden
                            />
                            {t('galleriesPage.backToGalleries')}
                        </Link>
                    </p>
                </SubpageLayout>
            </>
        );
    }

    const galleryMetaDesc = truncateMetaDescription(
        stripHtmlForMeta(String(detail?.description ?? '')) ||
            t('seo.galleryDetailFallback'),
    );
    const firstPhotoSrc = imageUrls[0]?.src;
    const showDownloadActions =
        useLiveApi && !staticGallery && imageUrls.length > 0;

    return (
        <>
            <PageSeo
                title={`${displayTitle || gallery.title} — ${t('header.rotaryAlt')}`}
                description={galleryMetaDesc}
                noindex
                ogImage={firstPhotoSrc}
            />
            <SubpageLayout
                title={displayTitle || gallery.title}
                subtitle={t('galleriesPage.photoCount', {
                    count: displayCount,
                })}
                contentClassName="galleries-page"
            >
                {staticGallery ? (
                    <>
                        <p className="mb-4">
                            <Link
                                to="/member-area/galleries"
                                className="gallery-detail-back-link d-inline-flex align-items-center gap-1"
                            >
                                <ChevronLeft
                                    size={20}
                                    strokeWidth={1.85}
                                    aria-hidden
                                />
                                {t('galleriesPage.backToAllGalleries')}
                            </Link>
                        </p>
                        <p className="gallery-detail-placeholder mb-0">
                            {t('galleriesPage.detailPlaceholder')}
                        </p>
                    </>
                ) : (
                    <>
                        <div className="gallery-detail-toolbar d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
                            <Link
                                to="/member-area/galleries"
                                className="gallery-detail-back-link d-inline-flex align-items-center gap-1"
                            >
                                <ChevronLeft
                                    size={20}
                                    strokeWidth={1.85}
                                    aria-hidden
                                />
                                {t('galleriesPage.backToAllGalleries')}
                            </Link>
                            {showDownloadActions ? (
                                <button
                                    type="button"
                                    className="gallery-detail-download-btn btn btn-sm d-inline-flex align-items-center gap-1 ms-auto"
                                    onClick={handleGalleryDownloadClick}
                                    disabled={Boolean(downloadModal)}
                                >
                                    <Download
                                        size={15}
                                        strokeWidth={2}
                                        aria-hidden
                                    />
                                    {t('galleriesPage.downloadGalleryButton')}
                                </button>
                            ) : null}
                        </div>

                        {detail?.description ? (
                            <div className="gallery-detail-description mb-4">
                                <p className="mb-0 text-secondary">
                                    {detail.description}
                                </p>
                            </div>
                        ) : null}

                        {imageUrls.length > 0 ? (
                            <section
                                className="news-detail-gallery"
                                aria-label={displayTitle || gallery.title}
                            >
                                <ul className="news-detail-gallery-grid list-unstyled row row-cols-2 row-cols-lg-4 g-3 mb-0">
                                    {imageUrls.map((img, index) => (
                                        <li key={img.id} className="col">
                                            <div className="gallery-detail-photo-wrap">
                                                <a
                                                    href={img.src}
                                                    className="news-detail-gallery-link"
                                                    data-fancybox={fancyboxGroup}
                                                    data-caption={
                                                        img.alt ||
                                                        displayTitle ||
                                                        gallery.title
                                                    }
                                                >
                                                    <img
                                                        src={img.thumb}
                                                        alt={img.alt || ''}
                                                        className="news-detail-gallery-thumb"
                                                        loading="lazy"
                                                    />
                                                </a>
                                                {showDownloadActions ? (
                                                    <button
                                                        type="button"
                                                        className="gallery-detail-photo-download-btn"
                                                        onClick={() =>
                                                            handleSingleImageDownload(
                                                                img,
                                                                index,
                                                            )
                                                        }
                                                        disabled={
                                                            downloadingImageId ===
                                                            img.id
                                                        }
                                                        aria-label={t(
                                                            'galleriesPage.downloadSingleImageAria',
                                                        )}
                                                    >
                                                        <Download
                                                            size={14}
                                                            strokeWidth={2}
                                                            aria-hidden
                                                        />
                                                    </button>
                                                ) : null}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        ) : (
                            <p className="gallery-detail-placeholder mb-0 text-secondary">
                                {t('galleriesPage.noPhotosInGallery')}
                            </p>
                        )}
                    </>
                )}
                <GalleryDownloadConfirmModal
                    open={Boolean(downloadModal)}
                    galleryTitle={downloadModal?.gallery.title || ''}
                    phase={downloadModal?.phase || 'loading'}
                    downloadStep={downloadModal?.downloadStep || 'fetching'}
                    fileCount={downloadModal?.fileCount || 0}
                    totalBytes={downloadModal?.totalBytes || 0}
                    error={downloadModal?.error}
                    onClose={closeDownloadModal}
                    onConfirm={handleDownloadConfirm}
                />
            </SubpageLayout>
        </>
    );
}
